import { updateSessionWithResult } from "@iewcasia/Services/SessionService";
import { sanitizePostAuthReturnUrl } from "@iewcasia/Utilities/sanitizePostAuthReturnUrl";
import { safeRedirect } from "@insite/client-framework/Common/Utilities/safeRedirect";
import { addToChainAfter, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getAccount } from "@insite/client-framework/Services/AccountService";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getCart, GetCartApiParameter } from "@insite/client-framework/Services/CartService";
import {
    getSession as getSessionApi,
    UpdateSessionWithResultApiParameter,
} from "@insite/client-framework/Services/SessionService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    NavigateToReturnUrl,
    PopulateApiParameter,
    UpdateSession,
} from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

/** True when SSO/Sign-In appended forcereturnurl so dashboard landing must not win. */
const hasForceReturnUrl = (url?: string) => !!url && /(?:\?|&)forcereturnurl(?:&|#|$)/i.test(url);

/** Removes the forcereturnurl flag before redirect/compare. */
const stripForceReturnUrl = (url?: string): string | undefined => {
    if (!url) {
        return url;
    }

    return url
        .replace(/([?&])forcereturnurl(?=&|#|$)/gi, "$1")
        .replace(/\?&/, "?")
        .replace(/[?&]$/, "");
};

/** SSO from Sign In without ?returnUrl= becomes "/?forcereturnurl" — that must not beat Dashboard Is Homepage. */
const isHomeOrEmptyReturnUrl = (url: string | undefined, homePageUrl: string): boolean => {
    const stripped = stripForceReturnUrl(url);
    if (!stripped) {
        return true;
    }

    const returnPath = (stripped.split("?")[0] || "/").toLowerCase().replace(/\/$/, "") || "";
    const homePath = (homePageUrl.split("?")[0] || "/").toLowerCase().replace(/\/$/, "") || "";

    return returnPath === "" || returnPath === "/" || returnPath === homePath;
};

addToChainAfter(chain, PopulateApiParameter, props => {
    if (!props.apiParameter.additionalQueryStringParameters) {
        props.apiParameter.additionalQueryStringParameters = {};
    }
    props.apiParameter.additionalQueryStringParameters.isContinue = true;
});

replaceInChain(chain, UpdateSession, async props => {
    const newApiParameter: UpdateSessionWithResultApiParameter = { ...props.apiParameter };
    if (props.apiParameter.session.billToId) {
        newApiParameter.session.billTo = {
            id: props.parameter.billToId,
            isDefault: props.parameter.isDefault,
        } as BillToModel;
    }
    if (props.apiParameter.session.shipToId !== undefined) {
        newApiParameter.session.shipTo = { id: props.parameter.shipToId } as ShipToModel;
    }

    const result = await updateSessionWithResult(newApiParameter);
    if (result.successful) {
        props.apiResult = await getSessionApi({});
    } else {
        props.parameter.onError?.(result.errorMessage);
        return false;
    }
});

/**
 * Honor SSO ?returnUrl=...&forcereturnurl over dashboard/custom landing.
 * OOTB always prefers dashboard when set, which drops checkout/RFQ return after Change Customer.
 */
replaceInChain(chain, NavigateToReturnUrl, async props => {
    if (props.parameter.skipRedirect) {
        return;
    }

    const state = props.getState();
    if (props.apiParameter.session.shipToId === "") {
        window.location.href = `${getPageLinkByPageType(state, "AddressesPage")?.url}?isNewShipTo=true`;
        return;
    }

    // Drop Identity Server OIDC paths (/identity/callback, etc.) — not storefront pages.
    let returnUrl = sanitizePostAuthReturnUrl(props.parameter.returnUrl);
    const homePageUrl = getHomePageUrl(state);
    // Only force when SSO asked for a real page (checkout, etc.). "/?forcereturnurl" must yield to dashboard.
    const forceReturn =
        hasForceReturnUrl(returnUrl) && !isHomeOrEmptyReturnUrl(returnUrl, homePageUrl);

    const { userId } = getQueryStrings(state);
    const userSetupPageUrl = getPageLinkByPageType(state, "UserSetupPage")?.url;
    if (
        returnUrl &&
        userSetupPageUrl &&
        returnUrl.toLowerCase().indexOf(userSetupPageUrl.toLowerCase()) > -1 &&
        userId
    ) {
        try {
            await getAccount({ accountId: userId, expand: ["approvers", "roles"] });
        } catch (error) {
            window.location.href = getPageLinkByPageType(state, "UserListPage")!.url;
            return;
        }
    }

    const session = getSession(state);
    const defaultUrl = session.dashboardIsHomepage
        ? getPageLinkByPageType(state, "MyAccountPage")?.url
        : session.customLandingPage;

    if (defaultUrl && !forceReturn) {
        window.location.href = defaultUrl;
        return;
    }

    returnUrl = stripForceReturnUrl(returnUrl);

    const checkoutShippingUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const returnPath = returnUrl?.split("?")[0]?.toLowerCase();
    if (returnPath && returnPath === checkoutShippingUrl?.toLowerCase()) {
        const cartResult = await getCart({
            cartId: API_URL_CURRENT_FRAGMENT,
            expand: ["cartLines", "hiddenproducts"],
        } as GetCartApiParameter);

        const { canCheckOut, canBypassCheckoutAddress } = cartResult.cart;
        const checkoutReviewAndSubmitUrl = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
        const cartUrl = getPageLinkByPageType(state, "CartPage")?.url;

        if (!canCheckOut || session.isRestrictedProductExistInCart) {
            returnUrl = cartUrl;
        } else if (canBypassCheckoutAddress) {
            returnUrl = checkoutReviewAndSubmitUrl;
        }
    }

    safeRedirect(returnUrl || homePageUrl, homePageUrl);
});
